// src/modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionEmailInputPage.tsx

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Mail, Loader2 } from "lucide-react";
import type { RootState } from "../../../../../redux/store";
import { useAppDispatch } from "../../../../../redux/useDispatch";
import {
    setNewSubscriptionEmail,
    setNewSubscriptionEmailVerified,
    setNewSubscriptionId,
} from "../../../../../redux/slice/subscription/subscriptionSlice";
import { useInitiateCustomerAgreementVerificationMutation } from "../../../../../api/payments/paymentsApiSlice";
import { KeyHandoverPageLayout } from "../../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../../components/KeyHandoverContentWrapper";
import { Spinner } from "../../../../../components/ui/spinner";
import { Input } from "../../../../../components/ui/input";
import { Button } from "../../../../../components/ui/button";
import {
    cleanEmail,
    getEmailSuggestion,
    isEmailAcceptable,
} from "../../../../../util/validation";
import routes from "../../../../../routes/routes";
import { useLocationHydration } from "../../hooks/useLocationHydration";
import { useSubscriptionHydration } from "../../hooks/useSubscriptionHydration";
import { useSubscriptionUrlSync } from "../../hooks/useSubscriptionUrlSync";

/**
 * Email Input Content
 * Handles email input and verification code sending
 */
function EmailInputContent() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const hydratedRef = React.useRef(false);

    const locationHydrated = useLocationHydration();

    // Hydrate subscription state from URL
    useSubscriptionHydration();

    // Sync subscription state back to URL
    useSubscriptionUrlSync(hydratedRef);

    // Mark as hydrated after first render
    React.useEffect(() => {
        if (!hydratedRef.current) {
            hydratedRef.current = true;
        }
    }, []);

    const email = useSelector(
        (state: RootState) => state.subscription.newSubscription.email
    );

    const [initiateVerification] = useInitiateCustomerAgreementVerificationMutation();

    const [localEmail, setLocalEmail] = useState(email || "");
    const [emailError, setEmailError] = useState<string | null>(null);
    const [isSendingCode, setIsSendingCode] = useState(false);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalEmail(value);
        setEmailError(null);
    };

    const handleSendVerificationCode = async () => {
        // Validate email
        const cleanedEmail = cleanEmail(localEmail);
        if (!cleanedEmail) {
            setEmailError(
                t("book_location.keys.subscription_email.error_required", {
                    defaultValue: "E-Mail ist erforderlich",
                })
            );
            return;
        }

        const suggestion = getEmailSuggestion(cleanedEmail);
        if (suggestion) {
            setEmailError(
                t("book_location.personal_data.validation.email_typo_suggestion", {
                    suggestion,
                })
            );
            return;
        }

        if (!isEmailAcceptable(cleanedEmail)) {
            setEmailError(
                t("book_location.keys.subscription_email.error_invalid", {
                    defaultValue: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
                })
            );
            return;
        }

        setIsSendingCode(true);
        setEmailError(null);

        try {
            // The agreement id arrives with the confirmed code, not here.
            await initiateVerification({ email: cleanedEmail }).unwrap();

            dispatch(setNewSubscriptionEmail(cleanedEmail));
            dispatch(setNewSubscriptionEmailVerified(false));
            dispatch(setNewSubscriptionId(null));

            const searchParams = new URLSearchParams(location.search);
            searchParams.delete("subscriptionId");
            searchParams.set("email", cleanedEmail);

            // Navigate to verify code page
            navigate({
                pathname: routes.keyHandoverSubscriptionVerifyCodePath,
                search: `?${searchParams.toString()}`,
            });
        } catch (error) {
            setEmailError(
                t("book_location.keys.subscription_email.error_send_failed", {
                    defaultValue: "Fehler beim Senden des Codes. Bitte versuchen Sie es erneut.",
                })
            );
        } finally {
            setIsSendingCode(false);
        }
    };

    if (!locationHydrated) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6 max-w-lg mx-auto">
            {/* Intro text */}
            <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                    <Mail className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">
                    {t("book_location.keys.subscription_email.title", {
                        defaultValue: "E-Mail-Adresse bestätigen",
                    })}
                </h2>
                <p className="text-sm text-slate-600">
                    {t("book_location.keys.subscription_email.description", {
                        defaultValue:
                            "Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Bestätigungscode.",
                    })}
                </p>
            </div>

            {/* Email input */}
            <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-slate-700">
                    {t("book_location.keys.subscription_email.label", {
                        defaultValue: "E-Mail-Adresse",
                    })}
                </label>
                <Input
                    id="email"
                    type="email"
                    placeholder="name@beispiel.de"
                    value={localEmail}
                    onChange={handleEmailChange}
                    error={Boolean(emailError)}
                    disabled={isSendingCode}
                    className="w-full"
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && localEmail.trim()) {
                            handleSendVerificationCode();
                        }
                    }}
                />
                {emailError && (
                    <p className="text-sm text-red-600 mt-1">{emailError}</p>
                )}
            </div>

            {/* Send code button */}
            <Button
                onClick={handleSendVerificationCode}
                disabled={isSendingCode || !localEmail.trim()}
                className="w-full"
            >
                {isSendingCode ? (
                    <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("book_location.keys.subscription_email.sending", {
                            defaultValue: "Sende Code...",
                        })}
                    </>
                ) : (
                    t("book_location.keys.subscription_email.send_button", {
                        defaultValue: "Bestätigungscode senden",
                    })
                )}
            </Button>
        </div>
    );
}

/**
 * Subscription Email Input Page
 * 
 * Flow 2: New Subscription - Step 2
 * User enters email address to receive verification code
 */
const SubscriptionEmailInputPage = () => {
    const { t } = useTranslation();

    return (
        <KeyHandoverPageLayout
            headerText={t("book_location.keys.subscription_email.header_text", {
                defaultValue: "E-Mail bestätigen",
            })}
            backTo={routes.keyHandoverSubscriptionPlanSelectionPath}
            bottomActions={null}
        >
            <KeyHandoverContentWrapper>
                <EmailInputContent />
            </KeyHandoverContentWrapper>
        </KeyHandoverPageLayout>
    );
};

export default SubscriptionEmailInputPage;
